import React, { useEffect } from 'react';
import { Space, Card, Input, Empty } from 'antd';

import { SearchOutlined } from '@ant-design/icons';
import { StyledTable, TablePagination } from '../../components/styled/StyledTable';
import { useAssetAuditRetrievalQuery } from '../../../../../graphql/assetAudit.generated';
import { getColumns } from './TableColumns';
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown';
import useTableRequest from '../../../../hooks/useTableRequest/useTableRequest';
import utilService from '../../../../../utils/util.service';
import { FilterOptions, OperatorType } from './FilterOptions/FilterOptions';
import { ComponentSpinner } from '../../../../shared/ComponentSpinner';
import { useTranslation } from '../../../../../i18n/useTranslation';

const { debounce } = utilService;

interface ChangeLogsProps {
    urn: string;
}

const ChangeLogs = ({ urn }: ChangeLogsProps) => {
    const { t } = useTranslation();
    const {
        tableRequest,
        hasNextPage,
        hasPrevPage,
        onApplyFilter,
        onResetFilter,
        setTablePagination,
        fetchNextPage,
        fetchPrevPage,
        renderTextPagination,
    } = useTableRequest({
        urn,
        filters: [],
        orderBy: '-time',
        pagination: {
            limit: 10,
            offset: 0,
            totalRecords: 0,
        },
    });

    const {
        data: queryData,
        loading,
        refetch,
    } = useAssetAuditRetrievalQuery({
        variables: {
            input: tableRequest,
        },
    });

    useEffect(() => {
        refetch({ input: tableRequest });
    }, [tableRequest, refetch]);

    useEffect(() => {
        setTablePagination((prev) => ({
            ...prev,
            totalRecords: queryData?.assetAuditRetrieval?.totalResults ?? 0,
        }));
    }, [queryData?.assetAuditRetrieval?.totalResults, setTablePagination]);

    const onSearch = debounce((event: React.ChangeEvent<HTMLInputElement>) => {
        const text: string = event.target.value;
        if (text) {
            onApplyFilter('message', text, OperatorType.CONTAIN);
        } else {
            onResetFilter('message');
        }
    }, 500);

    return (
        <Card>
            <Space.Compact block className="px-3 py-2 mt-1">
                <Input
                    allowClear
                    onChange={onSearch}
                    placeholder={t('Search by message')}
                    suffix={<SearchOutlined className="site-form-item-icon" />}
                />
            </Space.Compact>
            <Space className="pa-3 f-layout-wrap-xs" size="small">
                {FilterOptions.map((item) => (
                    <FilterDropdown
                        {...item}
                        key={item.id}
                        onApplyFilter={onApplyFilter}
                        onResetFilter={onResetFilter}
                    />
                ))}
            </Space>
            {loading && <ComponentSpinner />}
            <StyledTable
                loading={false}
                columns={getColumns()}
                dataSource={queryData?.assetAuditRetrieval?.result || []}
                rowKey="urn"
                pagination={false}
                locale={{
                    emptyText: <Empty description={t('No Data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                }}
            />
            <TablePagination
                pagination={{
                    fetchNextPage,
                    fetchPrevPage,
                    hasNextPage,
                    hasPrevPage,
                    renderTextPagination,
                }}
            />
        </Card>
    );
};

export default ChangeLogs;
